import React from "react";
import { ChevronDown } from "react-feather";
import { KgInspectorInstancePill } from "../../atoms/KgInspectorInstancePill";
import { KgInspectorNavLinkRow } from "../../atoms/KgInspectorNavLinkRow";
import {
  Chevron,
  CountBadge,
  HeaderRow,
  IconSlot,
  Label,
  LabelGroup,
  LinkRowStack,
  PillGrid,
  Section,
} from "./KgInspectorExpandableSection.styles";
import type { KgInspectorExpandableSectionProps } from "./KgInspectorExpandableSection.types";

export const KgInspectorExpandableSection: React.FC<
  KgInspectorExpandableSectionProps
> = (props) => {
  const {
    icon,
    label,
    isOpen = false,
    onToggle,
    compact = false,
    items,
    count: countProp,
    children,
  } = props;
  const variant = props.variant ?? "pills";
  const count = countProp ?? items.length;

  const toggleProps = onToggle
    ? {
        role: "button" as const,
        "aria-expanded": isOpen,
        onClick: onToggle,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        },
        tabIndex: 0,
      }
    : {};

  const header = (
    <>
      <LabelGroup>
        <IconSlot>{icon}</IconSlot>
        <Label $isOpen={isOpen}>{label}</Label>
        {count > 0 && <CountBadge>{count}</CountBadge>}
      </LabelGroup>
      {onToggle && (
        <Chevron $isOpen={isOpen}>
          <ChevronDown size={20} />
        </Chevron>
      )}
    </>
  );

  const expandedBody =
    variant === "linkRows" ? (
      <LinkRowStack>
        {items.map((item) => (
          <KgInspectorNavLinkRow
            key={item.canvasNodeId ?? item.label}
            link={item}
          />
        ))}
      </LinkRowStack>
    ) : (
      <PillGrid>
        {items.map((name) => (
          <KgInspectorInstancePill key={name} label={name} />
        ))}
      </PillGrid>
    );

  if (!isOpen) {
    return (
      <Section $isOpen={false} $compact={compact} {...toggleProps}>
        {header}
      </Section>
    );
  }

  return (
    <Section $isOpen $compact={compact}>
      <HeaderRow {...toggleProps}>{header}</HeaderRow>
      {children !== undefined ? children : (count > 0 && expandedBody)}
    </Section>
  );
};

export default KgInspectorExpandableSection;
